import { useEffect, useRef } from 'react';
import { UseFormReturn, useFormContext } from 'react-hook-form';
import { WarehouseUnit } from 'woofwoof-api';
import { parseStockQuantity } from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import {
  EMPTY_STOCK_VALUES,
  MedicationStockFormValues,
  StockHandoverChoice,
  openedTakingProductId,
} from './medicationStockValues';
import {
  StockIssueProduct,
  StockQuantityDefault,
  computeStockIssue,
  holdsStockDefault,
  inferStockHandover,
} from './stockIssueComputation';

interface SessionIssue {
  session: number;
  /** The Product of the taking: another one is a new taking (P7B-R3). */
  productId: string;
  /**
   * The taking is new — a Warehouse pick, a line opened on one, or another
   * Product than the form opened with — so its values are ours to work out.
   * The taking the form opened with is the vet's answer.
   */
  fresh: boolean;
  /** What this hook last set, so a value the vet changed is told apart. */
  handover: StockHandoverChoice | null;
  quantity: StockQuantityDefault | null;
  /** The pack of the Product last read: a count typed for it (P7B-R8). */
  pack: StockPack | null;
}

interface StockPack {
  productId: string;
  unit: WarehouseUnit;
  unitsPerPackage: number;
}

type StockForm = Pick<
  UseFormReturn<MedicationsFormModalValuesProps>,
  'getValues' | 'setValue'
>;

type SavedStock = Readonly<Partial<MedicationStockFormValues>> | undefined;

/** The session's first look: the taking on screen, new unless answered. */
function startSession(session: number, { getValues }: StockForm): SessionIssue {
  return {
    session,
    productId: getValues('stockProductId'),
    fresh:
      !getValues('stockWasSet') &&
      parseStockQuantity(getValues('stockQuantity')) === null,
    handover: null,
    quantity: null,
    pack: null,
  };
}

/** What the fields hold now, to be replaced as if this hook had set it. */
function heldQuantity({
  getValues,
}: Pick<StockForm, 'getValues'>): StockQuantityDefault {
  return {
    quantity: parseStockQuantity(getValues('stockQuantity')) ?? '',
    measure: getValues('stockMeasure'),
  };
}

/**
 * Step 1 picked another Warehouse Product (P7B-R3). Back on the one the form
 * opened with, its saved values return — its Batch too, as a same-Product
 * re-pick keeps it. Any other is a new taking: values that answered for an
 * earlier Product are ours to replace, and the opened taking's prescription
 * answer is cleared. An answer the vet gave a new taking carries across.
 */
function switchTaking(
  state: SessionIssue,
  productId: string,
  saved: SavedStock,
  { getValues, setValue }: StockForm,
): void {
  const answered = !state.fresh;
  state.productId = productId;
  if (productId === openedTakingProductId(saved)) {
    state.fresh = false;
    setValue(
      'stockQuantity',
      saved?.stockQuantity ?? EMPTY_STOCK_VALUES.stockQuantity,
    );
    setValue(
      'stockMeasure',
      saved?.stockMeasure ?? EMPTY_STOCK_VALUES.stockMeasure,
    );
    setValue(
      'stockHandover',
      saved?.stockHandover ?? EMPTY_STOCK_VALUES.stockHandover,
    );
    setValue('stockBatchId', saved?.stockBatchId ?? '');
    setValue('stockPrescription', saved?.stockPrescription ?? '');
    return;
  }
  state.fresh = true;
  if (answered) {
    state.handover = null;
    state.quantity = heldQuantity({ getValues });
    setValue('stockPrescription', '');
  }
}

/**
 * #692 rev B: keeps the "Magazyn" step's assumptions in step with the
 * dosing for a new taking — "Sposób wydania" from the frequency and
 * duration, "Ilość"/"Liczone w" from the dose, the course and the Product's
 * dispense mode. What goes onto the Drug Record Card the vet gives: never
 * the prescription of an Rx drug handed over (P7B-R9), nor "Podany w
 * placówce" for an Rx drug given once (`inferAsRx`: the form's flag, or true
 * while the Product is unread — P7B-R10, P7B-R13) — "Sposób wydania" then
 * stays blank and "Ilość" waits for it.
 * A value the vet changed stays theirs for the rest of the session — but a
 * quantity only for Products in the same pack (P7B-R8).
 * Inactive for a locked form; a new session (the dialog reopened on another
 * line) starts over.
 */
export function useStockIssueDefault(
  active: boolean,
  product: StockIssueProduct | undefined,
  session: number,
  inferAsRx: boolean,
): void {
  const { watch, getValues, setValue, getFieldState, formState } =
    useFormContext<MedicationsFormModalValuesProps>();
  const [
    productId,
    dosagePending,
    doseForm,
    quantityValue,
    frequencyType,
    frequencyValue,
    durationType,
    durationValue,
    handover,
  ] = watch([
    'stockProductId',
    'dosagePending',
    'form',
    'quantityValue',
    'frequencyType',
    'frequencyValue',
    'durationType',
    'durationValue',
    'stockHandover',
  ]);
  const saved: SavedStock = formState.defaultValues;
  const applied = useRef<SessionIssue | null>(null);
  const unit = product?.unit;
  const unitsPerPackage = product?.unitsPerPackage;
  const dispenseMode = product?.dispenseMode;

  useEffect(() => {
    if (!active) return;
    const form = { getValues, setValue };
    const kept = applied.current;
    const state =
      kept && kept.session === session ? kept : startSession(session, form);
    applied.current = state;
    if (state.productId !== productId) {
      switchTaking(state, productId, saved, form);
    }
    if (!state.fresh) return;
    if (unit && unitsPerPackage) {
      const { pack } = state;
      if (
        pack &&
        pack.productId !== productId &&
        (pack.unit !== unit || pack.unitsPerPackage !== unitsPerPackage)
      ) {
        // A count typed for another pack means another amount here; the
        // handover and prescription the vet gave still hold.
        state.quantity = heldQuantity(form);
      }
      state.pack = { productId, unit, unitsPerPackage };
    }

    const dosing = {
      dosagePending,
      form: doseForm,
      quantityValue,
      frequencyType,
      frequencyValue,
      durationType,
      durationValue,
    };
    let effective = handover;
    if (state.handover === null || handover === state.handover) {
      // An assumed handover the rules no longer assume is dropped too.
      effective = inferStockHandover(dosing, inferAsRx);
      if (effective !== handover) {
        setValue('stockHandover', effective, {
          // Only while the field shows an error: "Dalej" refused a blank
          // handover and the dosing then brought one, which clears it. One
          // dropped to blank is flagged by "Dalej", not at once.
          shouldValidate: Boolean(getFieldState('stockHandover').error),
        });
      }
      state.handover = effective;
    }
    const issue = computeStockIssue(
      dosing,
      effective,
      unit && unitsPerPackage
        ? { unit, unitsPerPackage, dispenseMode }
        : undefined,
    );
    if (
      holdsStockDefault(
        getValues('stockQuantity'),
        getValues('stockMeasure'),
        state.quantity,
      )
    ) {
      setValue('stockQuantity', issue.quantity, {
        // "Dalej" refused a blank "Ilość" before the vet said how the drug
        // left: the quantity that answer brings clears its error.
        shouldValidate: Boolean(getFieldState('stockQuantity').error),
      });
      setValue('stockMeasure', issue.measure);
      state.quantity = { quantity: issue.quantity, measure: issue.measure };
    }
  }, [
    active,
    session,
    saved,
    productId,
    unit,
    unitsPerPackage,
    dispenseMode,
    dosagePending,
    doseForm,
    quantityValue,
    frequencyType,
    frequencyValue,
    durationType,
    durationValue,
    handover,
    inferAsRx,
    getValues,
    setValue,
    getFieldState,
  ]);
}
